'use client';
import React, { useState } from 'react';
import { QrCode, Search, Download, Eye, CheckCircle2, XCircle, AlertCircle, MapPin } from 'lucide-react';

interface Parcel {
  id: string;
  qrCode: string;
  address: string;
  county: string;
  owner: string;
  area: string;
  status: 'registered' | 'unregistered' | 'disputed';
  taxStatus: 'paid' | 'overdue' | 'exempt';
  registeredDate: string;
  valuation: string;
  coordinates: string;
}

const PARCELS: Parcel[] = [
  { id: 'LR-MON-0042', qrCode: 'QR-MON-42-2024', address: '14 Broad St, Monrovia', county: 'Montserrado', owner: 'Kollie Trading Co.', area: '0.34 ha', status: 'registered', taxStatus: 'paid', registeredDate: '12 Mar 2023', valuation: '$48,500', coordinates: '6.300°N 10.797°W' },
  { id: 'LR-MON-0117', qrCode: 'QR-MON-117-2024', address: '88 Tubman Blvd, Monrovia', county: 'Montserrado', owner: 'Mary Pewee', area: '0.12 ha', status: 'registered', taxStatus: 'overdue', registeredDate: '05 Jul 2022', valuation: '$22,000', coordinates: '6.312°N 10.804°W' },
  { id: 'LR-NIM-0008', qrCode: 'QR-NIM-8-2024', address: 'Ganta Market Zone B', county: 'Nimba', owner: 'Unknown', area: '1.20 ha', status: 'unregistered', taxStatus: 'overdue', registeredDate: '—', valuation: 'Unassessed', coordinates: '7.228°N 8.988°W' },
  { id: 'LR-BON-0031', qrCode: 'QR-BON-31-2024', address: 'Gbarnga Central Plot 3', county: 'Bong', owner: 'Bong County Dev. Corp.', area: '2.50 ha', status: 'registered', taxStatus: 'exempt', registeredDate: '18 Jan 2021', valuation: '$135,000', coordinates: '6.994°N 9.472°W' },
  { id: 'LR-LOF-0019', qrCode: 'QR-LOF-19-2024', address: 'Voinjama Riverside', county: 'Lofa', owner: 'Contested — 2 claimants', area: '0.78 ha', status: 'disputed', taxStatus: 'overdue', registeredDate: '22 Sep 2020', valuation: '$31,200', coordinates: '8.422°N 9.748°W' },
  { id: 'LR-GBS-0055', qrCode: 'QR-GBS-55-2024', address: 'Buchanan Port Zone', county: 'Grand Bassa', owner: 'LPA Holdings', area: '5.10 ha', status: 'registered', taxStatus: 'paid', registeredDate: '30 Apr 2022', valuation: '$890,000', coordinates: '5.879°N 10.046°W' },
  { id: 'LR-MAR-0022', qrCode: 'QR-MAR-22-2024', address: 'Kakata Industrial Blvd', county: 'Margibi', owner: 'Unknown', area: '0.65 ha', status: 'unregistered', taxStatus: 'overdue', registeredDate: '—', valuation: 'Unassessed', coordinates: '6.528°N 10.348°W' },
  { id: 'LR-MRY-0004', qrCode: 'QR-MRY-4-2024', address: 'Harper Coastal Strip', county: 'Maryland', owner: 'Maryland Fisheries Ltd.', area: '3.20 ha', status: 'registered', taxStatus: 'paid', registeredDate: '14 Feb 2023', valuation: '$215,000', coordinates: '4.375°N 7.716°W' },
  { id: 'LR-SIN-0011', qrCode: 'QR-SIN-11-2024', address: 'Greenville Market District', county: 'Sinoe', owner: 'Sinoe Commerce Group', area: '0.90 ha', status: 'registered', taxStatus: 'overdue', registeredDate: '08 Nov 2021', valuation: '$67,400', coordinates: '5.015°N 9.039°W' },
  { id: 'LR-GGH-0003', qrCode: 'QR-GGH-3-2024', address: 'Zwedru Central Zone', county: 'Grand Gedeh', owner: 'Grand Gedeh Agri Ltd.', area: '8.40 ha', status: 'registered', taxStatus: 'paid', registeredDate: '25 Jun 2020', valuation: '$320,000', coordinates: '5.836°N 8.130°W' },
];

const statusIcon = {
  registered: <CheckCircle2 size={13} style={{ color: '#34d399' }} />,
  unregistered: <XCircle size={13} style={{ color: '#f87171' }} />,
  disputed: <AlertCircle size={13} style={{ color: '#fbbf24' }} />,
};

const statusColor = {
  registered: { color: '#34d399', bg: 'rgba(52,211,153,0.12)' },
  unregistered: { color: '#f87171', bg: 'rgba(248,113,113,0.12)' },
  disputed: { color: '#fbbf24', bg: 'rgba(251,191,36,0.12)' },
};

const taxColor = {
  paid: '#34d399',
  overdue: '#f87171',
  exempt: '#94a3b8',
};

interface ParcelRegistryTableProps {
  onSelectParcel: (id: string) => void;
}

export default function ParcelRegistryTable({ onSelectParcel }: ParcelRegistryTableProps) {
  const [search, setSearch] = useState('');
  const [scanningId, setScanningId] = useState<string | null>(null);

  const filtered = PARCELS.filter((p) =>
    !search ||
    p.id.toLowerCase().includes(search.toLowerCase()) ||
    p.owner.toLowerCase().includes(search.toLowerCase()) ||
    p.county.toLowerCase().includes(search.toLowerCase())
  );

  const handleScan = (id: string) => {
    setScanningId(id);
    setTimeout(() => setScanningId(null), 1500);
  };

  return (
    <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--dark-border)' }}>
        <div className="flex items-center gap-2">
          <QrCode size={16} style={{ color: 'var(--accent)' }} />
          <span className="text-sm font-semibold" style={{ color: 'var(--dark-text)' }}>QR-Tagged Parcel Registry</span>
          <span className="text-xs px-2 py-0.5 rounded-full font-mono-data" style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)' }}>
            {filtered.length} parcels
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--dark-muted)' }} />
            <input
              type="text"
              placeholder="Search parcels…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 pr-3 py-1.5 rounded-md text-xs outline-none"
              style={{ backgroundColor: 'var(--dark-elevated)', color: 'var(--dark-text)', border: '1px solid var(--dark-border)', width: '200px' }}
            />
          </div>
          <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors hover:opacity-80"
            style={{ backgroundColor: 'rgba(212,160,23,0.15)', color: 'var(--accent)', border: '1px solid rgba(212,160,23,0.3)' }}>
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full text-xs">
          <thead>
            <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
              {['Parcel ID', 'QR Code', 'Address', 'County', 'Owner', 'Area', 'Status', 'Tax Status', 'Valuation', 'Actions'].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--dark-muted)', backgroundColor: 'var(--dark-elevated)' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((parcel, i) => {
              const sc = statusColor[parcel.status];
              const isScanning = scanningId === parcel.id;
              return (
                <tr
                  key={parcel.id}
                  className="transition-colors hover:bg-white/3"
                  style={{ borderBottom: '1px solid var(--dark-border)', backgroundColor: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)' }}
                >
                  <td className="px-4 py-3 font-bold font-mono-data whitespace-nowrap" style={{ color: 'var(--accent)' }}>{parcel.id}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: isScanning ? 'rgba(212,160,23,0.3)' : 'rgba(212,160,23,0.1)', transition: 'background-color 0.3s' }}
                      >
                        <QrCode size={12} style={{ color: 'var(--accent)' }} />
                      </div>
                      <span style={{ color: 'var(--dark-muted)' }}>{parcel.qrCode}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 max-w-xs">
                    <div className="flex items-center gap-1">
                      <MapPin size={11} style={{ color: 'var(--dark-muted)', flexShrink: 0 }} />
                      <span className="truncate" style={{ color: 'var(--dark-text)' }}>{parcel.address}</span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>{parcel.coordinates}</p>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" style={{ color: 'var(--dark-text)' }}>{parcel.county}</td>
                  <td className="px-4 py-3 max-w-xs">
                    <span className="truncate block" style={{ color: parcel.owner === 'Unknown' ? '#f87171' : 'var(--dark-text)' }}>{parcel.owner}</span>
                  </td>
                  <td className="px-4 py-3 font-mono-data whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>{parcel.area}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full w-fit" style={{ backgroundColor: sc.bg, color: sc.color }}>
                      {statusIcon[parcel.status]}
                      {parcel.status.charAt(0).toUpperCase() + parcel.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="font-semibold" style={{ color: taxColor[parcel.taxStatus] }}>
                      {parcel.taxStatus.charAt(0).toUpperCase() + parcel.taxStatus.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono-data whitespace-nowrap" style={{ color: parcel.valuation === 'Unassessed' ? '#f87171' : 'var(--dark-text)' }}>
                    {parcel.valuation}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleScan(parcel.id)}
                        className="p-1.5 rounded-md transition-colors hover:bg-white/10"
                        style={{ color: isScanning ? 'var(--accent)' : 'var(--dark-muted)' }}
                        title="Simulate QR Scan"
                      >
                        <QrCode size={13} />
                      </button>
                      <button
                        onClick={() => onSelectParcel(parcel.id)}
                        className="p-1.5 rounded-md transition-colors hover:bg-white/10"
                        style={{ color: 'var(--dark-muted)' }}
                        title="View Ownership Chain"
                      >
                        <Eye size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
